/**
 * Wilson score interval for a binomial proportion.
 *
 * Used to estimate "is this block actually liked or disliked ?" while
 * penalizing low sample sizes. A block with 2 dislikes and a block
 * with 50 dislikes should NOT be treated as equally informative.
 *
 * References:
 *   https://en.wikipedia.org/wiki/Binomial_proportion_confidence_interval#Wilson_score_interval
 *
 * z = 1.96 → 95% confidence (default).
 * z = 1.0  → ~68% confidence (less conservative, more reactive).
 */

const DEFAULT_Z = 1.96;

/**
 * Wilson interval for proportion p = positives / total.
 * Returns { lower, upper, center } all in [0, 1].
 * Returns { lower: 0, upper: 1, center: 0.5 } for total === 0.
 */
export function wilsonInterval(positives, total, z = DEFAULT_Z) {
  if (!total || total <= 0) return { lower: 0, upper: 1, center: 0.5 };
  const n = total;
  const p = positives / n;
  const z2 = z * z;
  const denom = 1 + z2 / n;
  const center = (p + z2 / (2 * n)) / denom;
  const margin = (z * Math.sqrt((p * (1 - p) + z2 / (4 * n)) / n)) / denom;
  return {
    lower: Math.max(0, center - margin),
    upper: Math.min(1, center + margin),
    center
  };
}

/**
 * Returns a confidence in [0, 1] = 1 minus the interval width.
 * 0 → no data, totally uncertain. 1 → very confident.
 */
export function wilsonConfidence(positives, total, z = DEFAULT_Z) {
  const { lower, upper } = wilsonInterval(positives, total, z);
  return Math.max(0, Math.min(1, 1 - (upper - lower)));
}

/**
 * Signed score in [-1, +1] derived from Wilson interval.
 * Strategy: use the regularized center (≈ posterior mean of a Beta(1,1) prior)
 * mapped from [0,1] to [-1,+1] = 2*center - 1, then shrunk toward 0 by
 * the interval width (low confidence → score closer to 0).
 *
 * Examples (z=1.96):
 *   wilsonScore(0, 0)       → 0       (no data)
 *   wilsonScore(1, 0)       → +0.13   (1 like only, very uncertain)
 *   wilsonScore(10, 0)      → +0.79   (10 likes 0 dislikes, confident)
 *   wilsonScore(50, 0)      → +0.95   (very confident like)
 *   wilsonScore(5, 5)       → 0       (perfectly mixed → noise)
 *   wilsonScore(8, 2)       → +0.45   (mostly liked, decent confidence)
 *   wilsonScore(2, 8)       → -0.45   (mostly disliked, decent confidence)
 */
export function wilsonScore(likes, dislikes, z = DEFAULT_Z) {
  const total = (likes || 0) + (dislikes || 0);
  if (total <= 0) return 0;
  const { lower, upper, center } = wilsonInterval(likes, total, z);
  const score = 2 * center - 1;          // map [0,1] → [-1,+1]
  const confidence = 1 - (upper - lower); // [0,1]
  return score * confidence;
}

export { DEFAULT_Z };
